import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import { CustomTooltip } from './CustomTooltip';

const AXIS_TICK = { fill: '#64748b', fontSize: 12 };
const GRID = <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" strokeOpacity={0.7} />;

export const KpiTimeChart = ({
  data, xKey = 'label', series = [], type = 'linea', valueFormatter,
  axisFormatter, height = 360, stacked = false, showLegend = true,
  barLayout = 'horizontal', cellColors = null, testId,
}) => {
  if (!data || data.length === 0) {
    return <div className="py-16 text-center text-sm text-slate-400" data-testid={testId}>Sin datos para mostrar.</div>;
  }

  const tooltip = <Tooltip content={<CustomTooltip valueFormatter={valueFormatter} />} cursor={{ fill: 'rgba(100,116,139,0.06)' }} />;
  const legend = showLegend && series.length > 1
    ? <Legend iconType="circle" align="left" verticalAlign="bottom" wrapperStyle={{ paddingTop: 16, fontSize: 12 }} />
    : null;

  // Barras horizontales (categorías en eje Y)
  if (type === 'barras' && barLayout === 'vertical') {
    return (
      <div data-testid={testId}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#e2e8f0" strokeOpacity={0.7} />
            <XAxis type="number" axisLine={false} tickLine={false} tick={AXIS_TICK} tickFormatter={axisFormatter} />
            <YAxis type="category" dataKey={xKey} axisLine={false} tickLine={false} tick={{ ...AXIS_TICK, fontSize: 11 }} width={130} />
            {tooltip}{legend}
            {series.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[0, 4, 4, 0]} maxBarSize={30}>
                {cellColors && series.length === 1
                  ? data.map((_, idx) => <Cell key={idx} fill={cellColors[idx % cellColors.length]} />)
                  : null}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'barras') {
    return (
      <div data-testid={testId}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            {GRID}
            <XAxis dataKey={xKey} axisLine={false} tickLine={false} tick={AXIS_TICK} minTickGap={16} />
            <YAxis axisLine={false} tickLine={false} tick={AXIS_TICK} tickFormatter={axisFormatter} width={54} />
            {tooltip}{legend}
            {series.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={48} stackId={stacked ? 'a' : undefined}>
                {cellColors && series.length === 1
                  ? data.map((_, idx) => <Cell key={idx} fill={cellColors[idx % cellColors.length]} />)
                  : null}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'area') {
    return (
      <div data-testid={testId}>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`grad-${testId}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={s.color} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            {GRID}
            <XAxis dataKey={xKey} axisLine={false} tickLine={false} tick={AXIS_TICK} minTickGap={16} />
            <YAxis axisLine={false} tickLine={false} tick={AXIS_TICK} tickFormatter={axisFormatter} width={54} />
            {tooltip}{legend}
            {series.map((s) => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2}
                    fill={`url(#grad-${testId}-${s.key})`} stackId={stacked ? 'a' : undefined} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // línea (default)
  return (
    <div data-testid={testId}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          {GRID}
          <XAxis dataKey={xKey} axisLine={false} tickLine={false} tick={AXIS_TICK} minTickGap={16} />
          <YAxis axisLine={false} tickLine={false} tick={AXIS_TICK} tickFormatter={axisFormatter} width={54} />
          {tooltip}{legend}
          {series.map((s) => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                  strokeWidth={s.strong ? 3 : 2} dot={false} activeDot={{ r: 5 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
